const store = new Map()
const listeners = new Map()

export class Memory {

  /**
   * Returns a cached in-memory response for the given endpoint name, or
   * executes the API call exactly once (concurrent calls share one promise).
   * @param {string} name - Unique endpoint/cache key
   * @param {Object} request - Request payload passed to the API function
   * @param {Function} api - Function that returns the API promise, e.g. (req) => this.$api(req)
   * @param {boolean} force - If true, bypass cache and force a new request
   * @returns {Promise<*>}
   */
  static async get(name, request, api, force = false) {

    if(!force && store.has(name)) {
      return store.get(name)
    }

    const promise = (async () => {
      try {
        return await api(request)
      } catch (error) {
        store.delete(name)
        throw error
      }
    })()

    store.set(name, promise)

    // Only a forced refresh means the data actually changed, notifying on every
    // call would loop back through the subscribers reading the same endpoint
    if(force)
      promise.then(data => Memory.notify(name, data)).catch(() => {})

    return promise

  }

  /**
   * Remove a cached endpoint from memory. Call without arguments to clear all.
   * @param {string|null} name - Cache key to invalidate, or null/undefined for all
   */
  static invalidate(name = null) {

    if(name === null) {
      store.clear()
      Array.from(listeners.keys()).forEach(key => Memory.notify(key))
    } else {
      store.delete(name)
      Memory.notify(name)
    }

  }

  /**
   * Listen for changes of a cached endpoint (forced refresh or invalidation).
   * @param {string} name - Cache key to watch
   * @param {Function} callback - Called with the fresh data, or undefined on invalidation
   * @returns {Function} Unsubscribe function
   */
  static subscribe(name, callback) {

    if(!listeners.has(name))
      listeners.set(name, new Set())

    listeners.get(name).add(callback)

    return () => listeners.get(name)?.delete(callback)

  }

  /**
   * Notify all subscribers of the given endpoint.
   * @param {string} name
   * @param {*} data
   */
  static notify(name, data = undefined) {
    listeners.get(name)?.forEach(callback => callback(data))
  }

  /**
   * Check if an endpoint is currently cached.
   * @param {string} name
   * @returns {boolean}
   */
  static has(name) {
    return store.has(name)
  }

}
